import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Play, Shield, XCircle, Archive, ChevronDown, ChevronRight, UserPlus, Radio, Loader2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import FieldHelp from '@/components/help/FieldHelp';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import helpContent from '@/help/helpContent';
import sensitiveActions from '@/help/sensitiveActions';

const STATUS_LABELS = {
  draft: 'Borrador', planned: 'Planificado', dry_run_completed: 'Dry-run OK', test_ready: 'Listo para test',
  test_running: 'Enviando test...', test_success: 'Test exitoso', test_failed: 'Test fallido',
  mass_ready: 'Listo para envío masivo', mass_running: 'Enviando a audiencia...',
  mass_completed: 'Envío masivo completado', mass_partial: 'Envío parcial', mass_failed: 'Envío masivo fallido',
  cancelled: 'Cancelado', archived: 'Archivado',
};
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', planned: 'bg-blue-100 text-blue-700',
  dry_run_completed: 'bg-cyan-100 text-cyan-700', test_ready: 'bg-amber-100 text-amber-700',
  test_running: 'bg-yellow-100 text-yellow-700', test_success: 'bg-emerald-100 text-emerald-700',
  test_failed: 'bg-red-100 text-red-700',
  mass_ready: 'bg-purple-100 text-purple-700', mass_running: 'bg-fuchsia-100 text-fuchsia-700',
  mass_completed: 'bg-emerald-100 text-emerald-800', mass_partial: 'bg-orange-100 text-orange-700',
  mass_failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-50 text-red-500', archived: 'bg-gray-200 text-gray-500',
};

function Badge({ status }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100'}`}>{STATUS_LABELS[status] || status}</span>;
}

const SCHEDULE_STATUS_LABELS = {
  draft: 'Borrador', preflight_required: 'Necesita revisión', preflight_passed: 'Revisión OK',
  ready_for_delivery_future: 'Listo para enviar', cancelled: 'Cancelado', archived: 'Archivado',
};

export default function CampaignDelivery() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [testRecipients, setTestRecipients] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('runs');
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [creatingTR, setCreatingTR] = useState(false);
  const [form, setForm] = useState({ schedule_id: '', name: 'Delivery Plan', delivery_mode: 'dry_run' });
  const [trForm, setTrForm] = useState({ contact_id: '', channel: 'email', label: '' });
  const [testSendModal, setTestSendModal] = useState(null);
  const [testSendForm, setTestSendForm] = useState({ test_recipient_id: '', idempotency_key: '', confirm_text: '' });
  const [contacts, setContacts] = useState([]);
  const [massDispatchModal, setMassDispatchModal] = useState(null);
  const [massConfirmText, setMassConfirmText] = useState('');
  const [massProgress, setMassProgress] = useState(null);
  const [massSending, setMassSending] = useState(false);
  const [providerReadiness, setProviderReadiness] = useState(null);
  // Sprint 76 (7/N) — detalle por corrida: destinatarios/intentos/eventos
  // tenían API completa (S-anterior) sin ninguna forma de verlos; solo se
  // podía lanzar/monitorear el resumen agregado.
  const [drillTab, setDrillTab] = useState('');
  const [drillData, setDrillData] = useState({});
  const [drillLoading, setDrillLoading] = useState(false);
  const [archiving, setArchiving] = useState('');

  const load = useCallback(async () => {
    try {
      const [rRes, trRes, sRes] = await Promise.all([
        api.get('/tenant/campaign-delivery/runs'),
        api.get('/tenant/campaign-test-recipients'),
        api.get('/tenant/campaign-schedules?status=ready_for_delivery_future'),
      ]);
      setRuns(rRes.data.items || []);
      setTestRecipients(trRes.data.items || []);
      setSchedules(sRes.data.items || []);
    } catch { /* ignore */ }
    try { const cRes = await api.get('/tenant/contacts?page_size=50'); setContacts(cRes.data || []); } catch { /* ignore */ }
    try { const pRes = await api.get('/tenant/campaign-delivery/provider-readiness?channel=email_future'); setProviderReadiness(pRes.data); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    if (!form.schedule_id) { setError('Selecciona un schedule'); return; }
    setError('');
    try {
      const ikey = `plan-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      await api.post(`/tenant/campaign-schedules/${form.schedule_id}/delivery-plan`, {
        name: form.name, delivery_mode: form.delivery_mode, idempotency_key: ikey,
      });
      setCreating(false); load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error al crear plan'); }
  };

  const runDryRun = async (runId) => {
    try { await api.post(`/tenant/campaign-delivery/runs/${runId}/dry-run`); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error dry-run'); }
  };

  const cancelRun = async (runId) => {
    try { await api.patch(`/tenant/campaign-delivery/runs/${runId}/cancel`); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error al cancelar'); }
  };

  const loadDrill = async (runId, kind) => {
    setDrillTab(kind);
    const key = `${runId}:${kind}`;
    if (drillData[key]) return;
    setDrillLoading(true);
    try {
      const { data } = await api.get(`/tenant/campaign-delivery/runs/${runId}/${kind}`);
      setDrillData(d => ({ ...d, [key]: data.items || [] }));
    } catch (e) { setDrillData(d => ({ ...d, [key]: [] })); }
    finally { setDrillLoading(false); }
  };

  const archiveRun = async (runId) => {
    setArchiving(runId);
    try { await api.patch(`/tenant/campaign-delivery/runs/${runId}/archive`); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || 'No se pudo archivar'); }
    finally { setArchiving(''); }
  };

  const createTestRecipient = async () => {
    if (!trForm.contact_id || !trForm.channel) { setError('Completa contacto y canal'); return; }
    try {
      await api.post('/tenant/campaign-test-recipients', trForm);
      setCreatingTR(false); setTrForm({ contact_id: '', channel: 'email', label: '' }); load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error al crear test recipient'); }
  };

  const archiveTestRecipient = async (trId) => {
    try { await api.patch(`/tenant/campaign-test-recipients/${trId}/archive`); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const executeTestSend = async (runId) => {
    if (!testSendForm.test_recipient_id) { setError('Selecciona test recipient'); return; }
    if (testSendForm.confirm_text !== 'CONFIRMO PRUEBA') { setError('Escribe "CONFIRMO PRUEBA" para confirmar'); return; }
    try {
      const ikey = `ts-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      await api.post(`/tenant/campaign-delivery/runs/${runId}/test-send`, {
        test_recipient_id: testSendForm.test_recipient_id,
        idempotency_key: ikey, confirm_text: 'CONFIRMO PRUEBA',
      });
      setTestSendModal(null); load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error test-send'); }
  };

  const startAudienceDispatch = async (runId) => {
    if (massConfirmText !== 'CONFIRMO ENVIO AUDIENCIA') { setError('Escribe "CONFIRMO ENVIO AUDIENCIA"'); return; }
    setMassSending(true); setError('');
    try {
      const ikey = `mass-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      const { data } = await api.post(`/tenant/campaign-delivery/runs/${runId}/audience-dispatch`, {
        confirm_text: 'CONFIRMO ENVIO AUDIENCIA', batch_size: 25, idempotency_key: ikey,
      });
      setMassProgress(data); setMassDispatchModal(null); setMassConfirmText('');
      if (!data.complete) { await continueMassDispatch(runId); } else { load(); }
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error en audience dispatch'); }
    finally { setMassSending(false); }
  };

  const continueMassDispatch = async (runId) => {
    setMassSending(true);
    try {
      let complete = false;
      while (!complete) {
        const { data } = await api.post(`/tenant/campaign-delivery/runs/${runId}/audience-dispatch/continue`);
        setMassProgress(data);
        complete = data.complete;
      }
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error continuando dispatch'); }
    finally { setMassSending(false); }
  };

  const fetchMassProgress = async (runId) => {
    try { const { data } = await api.get(`/tenant/campaign-delivery/runs/${runId}/mass-progress`); setMassProgress(data); }
    catch { /* ignore */ }
  };

  if (loading) return <PantallaCargando data-testid="campaign-delivery-page" />;

  return (
    <div className="space-y-4" data-testid="campaign-delivery-page">
      <SectionHelp module="delivery" title={helpContent.delivery?.title || 'Entregas'} description={helpContent.delivery?.description} whatItDoesNot={helpContent.delivery?.whatItDoesNot} />
      <VerticalExampleBox module="delivery" />

      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded" data-testid="production-warning">
        Entrega masiva disponible. production_future sigue bloqueado. Envío masivo requiere dry-run + test-send previo.
      </div>

      {/* S19B: Provider readiness banner */}
      {providerReadiness && (
        <div className={`text-xs px-3 py-2 rounded border flex items-center gap-2 ${providerReadiness.mode === 'real' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-testid="provider-readiness-badge">
          <Radio className="w-3.5 h-3.5" />
          <span className="font-medium">{providerReadiness.mode === 'real' ? 'Envío real habilitado' : 'Modo simulado (default)'}</span>
          <span>{providerReadiness.note}</span>
          <span className="ml-auto text-[10px]" data-testid="adapter-mode-label">{providerReadiness.provider} — {providerReadiness.connection_status}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2"><Truck className="w-4 h-4" /> Entregas de Campañas</h2>
          <p className="text-sm text-[#64748B]">Dry-run, test-send y envío masivo gobernado.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab('runs')} className={`px-3 py-1 text-xs rounded ${tab === 'runs' ? 'bg-[#0F172A] text-white' : 'text-[#64748B]'}`}>Runs</button>
          <button onClick={() => setTab('test-recipients')} className={`px-3 py-1 text-xs rounded ${tab === 'test-recipients' ? 'bg-[#0F172A] text-white' : 'text-[#64748B]'}`}>Test Recipients</button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded" data-testid="delivery-error">{error}<button className="ml-2 underline" onClick={() => setError('')}>cerrar</button></div>}

      {tab === 'runs' && (
        <>
          <button onClick={() => setCreating(!creating)} data-testid="create-delivery-plan-btn" className="px-3 py-1.5 bg-[#F72585] text-white text-sm rounded-md hover:bg-[#D91A72]">+ Crear Delivery Plan</button>

          {creating && (
            <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="create-delivery-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FieldHelp label="Schedule (ready)" help={helpContent.delivery.fields.delivery_mode.help} htmlFor="delivery-schedule">
                  <select value={form.schedule_id} onChange={e => setForm(f => ({ ...f, schedule_id: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="delivery-schedule-select" id="delivery-schedule">
                    <option value="">-- Seleccionar --</option>
                    {schedules.map(s => <option key={s.id} value={s.id}>{s.planned_channel} | {SCHEDULE_STATUS_LABELS[s.status] || s.status}</option>)}
                  </select>
                </FieldHelp>
                <FieldHelp label="Nombre" help="Nombre descriptivo del delivery plan." htmlFor="delivery-name">
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" id="delivery-name" />
                </FieldHelp>
                <FieldHelp label={helpContent.delivery.fields.delivery_mode.label} help={helpContent.delivery.fields.delivery_mode.help} htmlFor="delivery-mode">
                  <select value={form.delivery_mode} onChange={e => setForm(f => ({ ...f, delivery_mode: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" id="delivery-mode">
                    <option value="dry_run">Dry Run</option>
                    <option value="test_send">Test Send</option>
                  </select>
                </FieldHelp>
              </div>
              <div className="flex gap-2">
                <button onClick={createPlan} className="px-3 py-1.5 bg-[#0F172A] text-white text-sm rounded" data-testid="delivery-save-btn">Crear Plan</button>
                <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm text-[#64748B]">Cancelar</button>
              </div>
            </div>
          )}

          {runs.length === 0 && !creating && (
            <EmptyStateGuide module="delivery" message="Aún no tienes entregas preparadas. En este sprint solo dry-run y test-send controlado; no se envía a toda la audiencia." ctaLabel="Crear delivery plan" onCta={() => setCreating(true)} />
          )}

          <div className="space-y-2">
            {runs.map(r => (
              <div key={r.id} className="bg-white border rounded-lg" data-testid={`delivery-run-${r.id}`}>
                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#F8FAFC]" onClick={() => { setExpanded(expanded === r.id ? null : r.id); setDrillTab(''); }}>
                  <div className="flex items-center gap-3 min-w-0">
                    {expanded === r.id ? <ChevronDown className="w-4 h-4 text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
                    <div>
                      <div className="flex items-center gap-2"><span className="text-sm font-medium">{r.name}</span><Badge status={r.status} /><span className="text-xs text-[#94A3B8]">{r.delivery_mode}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.adapter_mode === 'real' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{r.adapter_mode === 'real' ? 'Real' : 'Simulado'}</span>
                      </div>
                      <div className="text-xs text-[#94A3B8]">{r.channel} | {r.total_recipients} recipients</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {['draft', 'planned'].includes(r.status) && (
                      <SensitiveActionHelp action={sensitiveActions.delivery_dry_run} onConfirm={() => runDryRun(r.id)}>
                        <button title="Dry Run" className="p-1 rounded hover:bg-[#F1F5F9]"><Shield className="w-3.5 h-3.5 text-[#64748B]" /></button>
                      </SensitiveActionHelp>
                    )}
                    {['planned', 'dry_run_completed', 'test_ready', 'test_failed'].includes(r.status) && (
                      <SensitiveActionHelp action={sensitiveActions.delivery_test_send} onConfirm={() => { setTestSendModal(r.id); setTestSendForm({ test_recipient_id: '', idempotency_key: '', confirm_text: '' }); }}>
                        <button title="Test Send" data-testid={`test-send-btn-${r.id}`} className="p-1 rounded hover:bg-emerald-50"><Play className="w-3.5 h-3.5 text-emerald-600" /></button>
                      </SensitiveActionHelp>
                    )}
                    {['test_success', 'mass_ready'].includes(r.status) && (
                      <button title="Enviar a audiencia" data-testid={`mass-dispatch-btn-${r.id}`}
                        onClick={e => { e.stopPropagation(); setMassDispatchModal(r.id); setMassConfirmText(''); }}
                        className="p-1 rounded hover:bg-purple-50"><Radio className="w-3.5 h-3.5 text-purple-600" /></button>
                    )}
                    {['mass_running', 'mass_partial'].includes(r.status) && (
                      <button title="Continuar envío" data-testid={`mass-continue-btn-${r.id}`}
                        onClick={e => { e.stopPropagation(); continueMassDispatch(r.id); }}
                        disabled={massSending}
                        className="p-1 rounded hover:bg-purple-50 disabled:opacity-50"><Loader2 className={`w-3.5 h-3.5 text-purple-600 ${massSending ? 'animate-spin' : ''}`} /></button>
                    )}
                    {!['cancelled', 'archived', 'mass_running'].includes(r.status) && (
                      <SensitiveActionHelp action={sensitiveActions.delivery_cancel} onConfirm={() => cancelRun(r.id)}>
                        <button title="Cancelar" className="p-1 rounded hover:bg-red-50"><XCircle className="w-3.5 h-3.5 text-red-400" /></button>
                      </SensitiveActionHelp>
                    )}
                  </div>
                </div>
                {expanded === r.id && (
                  <div className="border-t p-3 bg-[#F8FAFC] text-xs space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>Total: <strong>{r.total_recipients}</strong></div>
                      <div>Eligible: <strong className="text-emerald-600">{r.eligible_recipients}</strong></div>
                      <div>Blocked: <strong className="text-red-500">{r.blocked_recipients}</strong></div>
                      <div>Test: <strong className="text-blue-500">{r.test_recipients}</strong></div>
                    </div>
                    {(r.sent_recipients > 0 || r.failed_recipients > 0) && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-t pt-2">
                        <div>Sent: <strong className="text-emerald-700">{r.sent_recipients}</strong></div>
                        <div>Failed: <strong className="text-red-600">{r.failed_recipients}</strong></div>
                        <div>Skipped: <strong className="text-amber-600">{r.skipped_recipients}</strong></div>
                        <div>Batch: <strong>{r.current_batch}/{r.total_batches}</strong></div>
                      </div>
                    )}
                    {r.status.startsWith('mass_') && r.total_batches > 0 && (
                      <div data-testid={`mass-progress-bar-${r.id}`}>
                        <div className="flex items-center justify-between text-[10px] text-[#64748B] mb-1">
                          <span>Progreso envío masivo</span>
                          <span>{r.current_batch}/{r.total_batches} batches</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full transition-all ${r.status === 'mass_completed' ? 'bg-emerald-500' : r.status === 'mass_failed' ? 'bg-red-500' : 'bg-purple-500'}`}
                            style={{ width: `${Math.min(100, (r.current_batch / r.total_batches) * 100)}%` }} />
                        </div>
                      </div>
                    )}
                    {r.dry_run_summary?.dry_run && <div className="text-emerald-600">Dry-run completado — ningún proveedor llamado</div>}
                    {r.mass_dispatch_summary?.status && <div className="text-purple-600">Envío masivo: {STATUS_LABELS[r.mass_dispatch_summary.status] || r.mass_dispatch_summary.status} — enviados={r.mass_dispatch_summary.sent} fallidos={r.mass_dispatch_summary.failed}</div>}
                    {r.error_message && <div className="text-red-600">{r.error_message}</div>}

                    {/* Sprint 76 (7/N) — detalle real de la corrida */}
                    <div className="flex items-center gap-1 border-t pt-2">
                      {['recipients', 'attempts', 'events'].map(kind => (
                        <button key={kind} onClick={() => loadDrill(r.id, kind)}
                          className={`px-2 py-1 rounded text-[11px] font-medium ${drillTab === kind ? 'bg-[#0F172A] text-white' : 'bg-white border text-[#64748B] hover:bg-[#F1F5F9]'}`}
                          data-testid={`drill-${kind}-btn-${r.id}`}>
                          {kind === 'recipients' ? 'Destinatarios' : kind === 'attempts' ? 'Intentos' : 'Eventos'}
                        </button>
                      ))}
                      {!['cancelled', 'archived'].includes(r.status) && (
                        <button onClick={() => archiveRun(r.id)} disabled={archiving === r.id}
                          className="ml-auto px-2 py-1 rounded text-[11px] text-gray-500 hover:bg-gray-100 flex items-center gap-1"
                          data-testid={`archive-run-btn-${r.id}`}>
                          <Archive className="w-3 h-3" /> {archiving === r.id ? 'Archivando...' : 'Archivar'}
                        </button>
                      )}
                    </div>

                    {drillTab && (
                      <div className="max-h-48 overflow-y-auto border rounded bg-white" data-testid={`drill-panel-${r.id}`}>
                        {drillLoading && !drillData[`${r.id}:${drillTab}`] ? (
                          <p className="text-[11px] text-[#94A3B8] p-2">Cargando...</p>
                        ) : (drillData[`${r.id}:${drillTab}`] || []).length === 0 ? (
                          <p className="text-[11px] text-[#94A3B8] p-2">Sin registros.</p>
                        ) : drillTab === 'recipients' ? (
                          (drillData[`${r.id}:${drillTab}`] || []).map(item => (
                            <div key={item.id} className="flex items-center justify-between px-2 py-1 border-b last:border-0 text-[11px]">
                              <span>{item.masked_identity} <span className="text-[#94A3B8]">({item.channel})</span></span>
                              <span className={item.delivery_status === 'sent' ? 'text-emerald-600' : item.delivery_status === 'failed' ? 'text-red-600' : 'text-gray-500'}>{item.delivery_status || item.eligibility_status}</span>
                            </div>
                          ))
                        ) : drillTab === 'attempts' ? (
                          (drillData[`${r.id}:${drillTab}`] || []).map(item => (
                            <div key={item.id} className="px-2 py-1 border-b last:border-0 text-[11px]">
                              <div className="flex items-center justify-between">
                                <span>#{item.attempt_number} · {item.provider_key || 'sin proveedor'}</span>
                                <span className={item.status === 'success' ? 'text-emerald-600' : item.status === 'failed' ? 'text-red-600' : 'text-gray-500'}>{item.status}</span>
                              </div>
                              {item.error_message && <p className="text-red-500">{item.error_message}</p>}
                            </div>
                          ))
                        ) : (
                          (drillData[`${r.id}:${drillTab}`] || []).map(item => (
                            <div key={item.id} className="px-2 py-1 border-b last:border-0 text-[11px]">
                              <span className="font-medium">{item.event_type}</span>
                              {item.message && <span className="text-[#64748B]"> — {item.message}</span>}
                              <span className="text-[#CBD5E1] ml-2">{item.created_at ? new Date(item.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'test-recipients' && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#0F172A]">Test Recipients</h3>
            <button onClick={() => setCreatingTR(!creatingTR)} className="px-3 py-1.5 bg-[#F72585] text-white text-xs rounded" data-testid="create-test-recipient-btn"><UserPlus className="w-3 h-3 inline mr-1" />Agregar</button>
          </div>
          {creatingTR && (
            <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="create-test-recipient-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FieldHelp label={helpContent.delivery.fields.test_recipient.label} help={helpContent.delivery.fields.test_recipient.help} htmlFor="tr-contact">
                  <select value={trForm.contact_id} onChange={e => setTrForm(f => ({ ...f, contact_id: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" id="tr-contact" data-testid="tr-contact-select">
                    <option value="">Seleccionar contacto...</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.display_name || `${c.first_name} ${c.last_name}`} ({c.primary_email || c.primary_phone || c.id.slice(0,8)})</option>)}
                  </select>
                </FieldHelp>
                <FieldHelp label="Canal" help="Canal por el que se enviará la prueba." htmlFor="tr-channel">
                  <select value={trForm.channel} onChange={e => setTrForm(f => ({ ...f, channel: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" id="tr-channel">
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms_future">SMS (futuro)</option>
                  </select>
                </FieldHelp>
                <FieldHelp label="Etiqueta" help="Etiqueta para identificar al test recipient." htmlFor="tr-label">
                  <input placeholder="Etiqueta (opcional)" value={trForm.label} onChange={e => setTrForm(f => ({ ...f, label: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" id="tr-label" />
                </FieldHelp>
              </div>
              <div className="flex gap-2">
                <button onClick={createTestRecipient} className="px-3 py-1.5 bg-[#0F172A] text-white text-sm rounded">Crear</button>
                <button onClick={() => setCreatingTR(false)} className="px-3 py-1.5 text-sm text-[#64748B]">Cancelar</button>
              </div>
              <p className="text-xs text-[#94A3B8]">¿No tienes contactos? <button className="text-[#F72585] underline" onClick={() => navigate('/tenant/marketing/contacts')}>Ir a Contactos</button></p>
            </div>
          )}
          {testRecipients.length === 0 && !creatingTR && <p className="text-sm text-[#94A3B8] py-4">No hay test recipients. Agrega uno para poder hacer test-send.</p>}
          <div className="space-y-1">
            {testRecipients.map(tr => (
              <div key={tr.id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-xs">
                <div><span className="font-medium">{tr.label || 'Sin etiqueta'}</span> <span className="text-[#94A3B8] ml-2">{tr.channel} | {tr.contact_id.slice(0,8)}...</span></div>
                <button onClick={() => archiveTestRecipient(tr.id)} className="text-red-400 hover:text-red-600 text-xs">Archivar</button>
              </div>
            ))}
          </div>
        </>
      )}

      {testSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="test-send-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-4">
            <h3 className="text-sm font-semibold mb-3">Test Send — Envío de prueba</h3>
            <p className="text-xs text-[#94A3B8] mb-3">Se enviará a UN solo contacto test. No se envía a la audiencia.</p>
            <div className="space-y-3">
              {providerReadiness?.mode === 'real' && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800" data-testid="real-delivery-warning">
                  Este envío usará proveedor real ({providerReadiness.provider}). El mensaje llegará al destinatario.
                </div>
              )}
              <select value={testSendForm.test_recipient_id} onChange={e => setTestSendForm(f => ({ ...f, test_recipient_id: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="test-send-recipient-select">
                <option value="">-- Seleccionar test recipient --</option>
                {testRecipients.filter(t => t.status === 'active').map(t => <option key={t.id} value={t.id}>{t.label || t.channel} | {t.contact_id.slice(0,8)}</option>)}
              </select>
              <div>
                <label className="text-xs text-[#334155]">Escribe &quot;CONFIRMO PRUEBA&quot; para continuar:</label>
                <input value={testSendForm.confirm_text} onChange={e => setTestSendForm(f => ({ ...f, confirm_text: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-1" placeholder="CONFIRMO PRUEBA" data-testid="test-send-confirm-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => executeTestSend(testSendModal)} disabled={testSendForm.confirm_text !== 'CONFIRMO PRUEBA'} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded disabled:opacity-50" data-testid="test-send-execute-btn">Enviar prueba</button>
              <button onClick={() => setTestSendModal(null)} className="px-3 py-1.5 text-sm text-[#64748B]">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {massDispatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="mass-dispatch-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Radio className="w-4 h-4 text-purple-600" /> Enviar a audiencia completa</h3>
            <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs text-purple-800 mb-3 space-y-1">
              <p className="font-medium">Se enviará a TODOS los contactos elegibles de la audiencia vinculada.</p>
              <p>Solo contactos con consent opted_in recibirán el mensaje. Se procesará en lotes de 25.</p>
              <p className="text-[10px] text-purple-600 italic">Esta acción queda registrada en auditoría y consume créditos del proveedor.</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#334155]">Escribe &quot;CONFIRMO ENVIO AUDIENCIA&quot; para confirmar:</label>
                <input value={massConfirmText} onChange={e => setMassConfirmText(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1" placeholder="CONFIRMO ENVIO AUDIENCIA"
                  data-testid="mass-dispatch-confirm-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => startAudienceDispatch(massDispatchModal)}
                disabled={massConfirmText !== 'CONFIRMO ENVIO AUDIENCIA' || massSending}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded disabled:opacity-50 flex items-center gap-1"
                data-testid="mass-dispatch-execute-btn">
                {massSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-3.5 h-3.5" />}
                Enviar a audiencia
              </button>
              <button onClick={() => { setMassDispatchModal(null); setMassConfirmText(''); }} className="px-3 py-1.5 text-sm text-[#64748B]">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
